function formatAmount(value) {
  if (value == null || value === "") return "0.00";
  const num = Number.parseFloat(String(value).replace(/,/g, ""));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function tryParseJson(text) {
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

const COLLECTION_LABELS = {
  purchaseorders: "purchase orders",
  salesorders: "sales orders",
  asns: "ASN",
  grns: "GRN",
  shipments: "shipment orders",
  items: "items",
};

function collectionLabel(collection) {
  return COLLECTION_LABELS[collection] || "records";
}

function detectRequestedFields(userText = "") {
  const q = userText.toLowerCase().replace(/quentity|quanity|quntity/g, "quantity");
  const fields = new Set();
  const wantsOnly = /\bonly\b|\bno\s+other\s+data\b/.test(q);
  // Treat "provide / give / tell / what is / show me the X" as a focused field ask.
  const focusedAsk =
    wantsOnly ||
    /\b(provide|give|tell|return|fetch|get)\b.{0,40}\b(the\s+)?(total\s*)?(price|amount|status|vendor|supplier|buyer|customer|owner|warehouse|quantity|qty|sku)/.test(
      q
    ) ||
    /\b(what|whats|what's)\s+(is\s+)?(the\s+)?/.test(q) ||
    /\bshow\s+me\s+(the\s+)?/.test(q);

  if (/\b(sku|skus|line\s*items?|item\s*list|which\s+sku)\b/.test(q)) {
    fields.add("sku");
  }
  if (/\b(description|sku\s*desc)/.test(q)) {
    fields.add("skuDescription");
  }

  // Specific quantity types first (open / ordered / received / expected)
  if (/\bopen\s*(quantity|qty)\b/.test(q)) fields.add("openQuantity");
  if (/\b(ordered|total\s*ordered)\s*(quantity|qty)\b/.test(q)) fields.add("quantity");
  if (/\breceived\s*(quantity|qty)\b/.test(q)) fields.add("receivedQuantity");
  if (/\bexpected\s*(quantity|qty)\b/.test(q)) fields.add("expectedQuantity");

  if (
    /\b(total\s*price|price\s*only|total\s*po\s*price|total\s*asn\s*price|total\s*amount|invoice\s*amount|total\s*spend|total\s*value|total\s*revenue)\b/.test(
      q
    ) ||
    (focusedAsk && /\b(price|amount|cost|spend|value|revenue)\b/.test(q) && !/\b(quantity|qty)\b/.test(q))
  ) {
    fields.add("totalPrice");
  }
  if (
    /\bstatus\b/.test(q) &&
    (focusedAsk || /\bwhat\s+is\s+(the\s+)?status\b/.test(q) || /\b(provide|give|tell|show).{0,20}\bstatus\b/.test(q))
  ) {
    fields.add("status");
  }
  // Generic quantity only when no specific qty type was requested
  if (
    !fields.has("openQuantity") &&
    !fields.has("receivedQuantity") &&
    !fields.has("expectedQuantity") &&
    (/\b(quantity|qty)\s*only\b/.test(q) ||
      (focusedAsk && /\b(quantity|qty)\b/.test(q) && !/\b(price|amount|status|sku)\b/.test(q)))
  ) {
    fields.add("quantity");
  }
  if (/\b(supplier|vendor)\b/.test(q) && focusedAsk) fields.add("supplier");
  if (/\b(buyer|customer|owner)\b/.test(q) && focusedAsk) fields.add("buyer");
  if (/\bwarehouse\b/.test(q) && focusedAsk) fields.add("warehouse");
  if (/\border\s*date\b/.test(q) && focusedAsk) fields.add("orderDate");
  if (/\bexpected\s*(receipt|delivery)\b/.test(q) && focusedAsk) fields.add("expectedReceipt");

  // If any focused field matched, keep the answer minimal.
  if (focusedAsk && fields.size) fields.add("minimal");
  if (wantsOnly) fields.add("minimal");

  return fields;
}

function getLineItems(doc) {
  if (Array.isArray(doc?.lineItems) && doc.lineItems.length) return doc.lineItems;
  if (Array.isArray(doc?.AsnDetail) && doc.AsnDetail.length) {
    return doc.AsnDetail.map((item) => ({
      skuId: item["SKU ID"] || item.skuId,
      description: item.Description || item["Description"] || item.description,
      orderedQty: item["Expected Qty"] ?? item.expectedQty,
      receivedQty: item["Received Qty"] ?? item.receivedQty,
      openQty: item.openQty,
      uom: item.UOM || item.uom || "",
      unitPrice: item.unitPrice,
    }));
  }
  // Shipment stops → flatten line items
  if (Array.isArray(doc?.stops) && doc.stops.length) {
    const items = [];
    for (const stop of doc.stops) {
      const lines = Array.isArray(stop?.lineItems) ? stop.lineItems : [];
      for (const li of lines) {
        const detail = li?.detail && typeof li.detail === "object" ? li.detail : {};
        items.push({
          skuId: li.skuId || detail.skuId,
          description: li.description || detail.description,
          orderedQty: li.orderedQty ?? detail.orderedQty ?? li.dispatchQty,
          receivedQty: li.receivedQty ?? detail.receivedQty,
          openQty: li.openQty ?? detail.openQty,
          uom: li.uom || detail.uom || "",
          unitPrice: li.unitPrice ?? detail.unitPrice,
        });
      }
    }
    if (items.length) return items;
  }
  if (Array.isArray(doc?.items) && doc.items.length) return doc.items;
  return [];
}

function shipmentStatusLabel(value) {
  if (value === 0 || `${value}` === "0") return "NEW";
  if (value === 1 || `${value}` === "1") return "OPEN";
  if (value === 2 || `${value}` === "2") return "LOADED";
  if (value === 7 || `${value}` === "7") return "CLOSED";
  if (value === 8 || `${value}` === "8") return "ON HOLD";
  if (value === 9 || `${value}` === "9") return "SHIPPED";
  if (value == null || value === "") return "N/A";
  return String(value).toUpperCase();
}

function normalizeLineItem(li) {
  const detail = li?.detail && typeof li.detail === "object" ? li.detail : {};
  return {
    skuId: li?.skuId || detail.skuId || li?.itemId || detail.itemId || li?.sku || "N/A",
    description: li?.description || detail.description || li?.name || detail.name || "N/A",
    orderedQty: li?.orderedQty ?? detail.orderedQty,
    receivedQty: li?.receivedQty ?? detail.receivedQty ?? detail.totalReceivedQty,
    openQty: li?.openQty ?? detail.openQty,
    uom: li?.uom || detail.uom || "",
    unitPrice: li?.unitPrice ?? detail.unitPrice ?? li?.asnUnitPrice,
  };
}

function formatSkuLines(doc, userText = "", title = "SKUs") {
  const items = getLineItems(doc).map(normalizeLineItem);
  const requested = detectRequestedFields(userText);
  const skuOnly =
    requested.has("sku") &&
    !requested.has("totalPrice") &&
    !requested.has("status") &&
    !requested.has("quantity") &&
    !requested.has("supplier") &&
    !requested.has("buyer") &&
    !requested.has("warehouse");

  if (!items.length) {
    return skuOnly ? "No SKU / line items found on this record." : "";
  }

  const lines = [`**${title} (${items.length}):**`, ""];
  for (const item of items) {
    if (skuOnly || (requested.has("sku") && requested.has("skuDescription"))) {
      lines.push(`- **SKU ID:** ${item.skuId}`);
      lines.push(`  - **SKU Description:** ${item.description}`);
    } else {
      lines.push(`- **${item.skuId}** — ${item.description}`);
      if (item.orderedQty != null) lines.push(`  - Ordered Qty: ${item.orderedQty}${item.uom ? ` ${item.uom}` : ""}`);
      if (item.receivedQty != null) lines.push(`  - Received Qty: ${item.receivedQty}`);
      if (item.unitPrice != null && item.unitPrice !== "") {
        lines.push(`  - Unit Price: ${formatAmount(item.unitPrice)}`);
      }
    }
    lines.push("");
  }
  return lines.join("\n").trim();
}

const FIELD_PRIORITY = [
  "openQuantity",
  "receivedQuantity",
  "expectedQuantity",
  "quantity",
  "totalPrice",
  "status",
  "supplier",
  "buyer",
  "warehouse",
  "orderDate",
  "expectedReceipt",
  "sku",
];

function applyFieldFilter(requestedFields, fieldMap) {
  if (!requestedFields?.size) return null;

  const filtered = [];
  for (const field of FIELD_PRIORITY) {
    if (requestedFields.has(field) && fieldMap[field]) {
      filtered.push(fieldMap[field]);
    }
  }

  if (filtered.length) return filtered;

  // "only" was asked but no known field matched — do not invent total price
  if (requestedFields.has("minimal")) return null;
  return null;
}

function formatSelectedFields(title, requestedFields, fieldMap, fullLines) {
  const hasSpecific =
    requestedFields.has("openQuantity") ||
    requestedFields.has("receivedQuantity") ||
    requestedFields.has("expectedQuantity") ||
    requestedFields.has("quantity") ||
    requestedFields.has("totalPrice") ||
    requestedFields.has("status") ||
    requestedFields.has("supplier") ||
    requestedFields.has("buyer") ||
    requestedFields.has("warehouse") ||
    requestedFields.has("orderDate") ||
    requestedFields.has("expectedReceipt");

  if (!hasSpecific && !requestedFields.has("minimal")) {
    return fullLines.join("\n");
  }

  const filtered = applyFieldFilter(requestedFields, fieldMap);
  if (filtered?.length) {
    return [`### ${title}`, "", ...filtered].join("\n");
  }

  return fullLines.join("\n");
}

function formatPurchaseOrder(doc, userText = "") {
  const po = doc.purchase_order || {};
  const supplier = doc.supplier || {};
  const buyer = doc.buyer || {};
  const orderId = po.orderId || doc.orderId || "Purchase Order";
  const requestedFields = detectRequestedFields(userText);
  const skuBlock = formatSkuLines(doc, userText, "SKUs");

  if (requestedFields.has("sku") && !requestedFields.has("totalPrice") && !requestedFields.has("status") && !requestedFields.has("openQuantity")) {
    return [`### ${orderId}`, "", skuBlock || "No SKU / line items found on this record."].join("\n");
  }

  const fieldMap = {
    totalPrice: `- **Total Price:** ${formatAmount(po.totalPOPrice)}`,
    status: `- **Status:** ${po.status ?? "N/A"}`,
    quantity: `- **Ordered Quantity:** ${po.totalOrderedQuantity ?? "N/A"}`,
    openQuantity: `- **Open Quantity:** ${po.openQuantity ?? "N/A"}`,
    supplier: `- **Supplier:** ${supplier.supplier || "N/A"}`,
    buyer: `- **Buyer:** ${buyer.buyer || "N/A"}`,
    warehouse: `- **Warehouse:** ${po.warehouse || "N/A"}`,
    orderDate: `- **Order Date:** ${po.purchaseOrderDate || "N/A"}`,
    expectedReceipt: `- **Expected Receipt:** ${po.expectedReceiptDate || "N/A"}`,
  };

  const lines = [
    `### ${orderId}`,
    "",
    fieldMap.status,
    fieldMap.totalPrice,
    fieldMap.quantity,
    fieldMap.openQuantity,
    fieldMap.warehouse,
    fieldMap.supplier,
    fieldMap.buyer,
    fieldMap.orderDate,
    fieldMap.expectedReceipt,
  ];

  if (requestedFields.size && !requestedFields.has("sku")) {
    return formatSelectedFields(orderId, requestedFields, fieldMap, lines);
  }

  if (skuBlock) {
    lines.push("", skuBlock);
  }

  return lines.join("\n");
}

function formatSalesOrder(doc, userText = "") {
  const so = doc.soDetails || doc.sales_order || doc.salesOrder || {};
  const invoice = doc.invoice || {};
  const orderId = so.soNumber || so.orderId || so.orderNumber || "Sales Order";
  const totalPrice = invoice.invoiceAmount || so.totalPrice || so.totalSOPrice;
  const requestedFields = detectRequestedFields(userText);
  const skuBlock = formatSkuLines(doc, userText, "SKUs");

  if (
    requestedFields.has("sku") &&
    !requestedFields.has("totalPrice") &&
    !requestedFields.has("status") &&
    !requestedFields.has("openQuantity")
  ) {
    return [`### ${orderId}`, "", skuBlock || "No SKU / line items found on this record."].join("\n");
  }

  const fieldMap = {
    totalPrice: `- **Total Price:** ${formatAmount(totalPrice)}`,
    status: `- **Status:** ${so.status ?? "N/A"}`,
    quantity: `- **Ordered Quantity:** ${so.totalOrderedQuantity ?? "N/A"}`,
    openQuantity: `- **Open Quantity:** ${so.openQuantity ?? "N/A"}`,
    buyer: `- **Owner:** ${so.owner || doc.destination?.destinationName || "N/A"}`,
    warehouse: `- **Warehouse:** ${doc.warehouse || so.warehouse || "N/A"}`,
    orderDate: `- **Order Date:** ${so.orderDate || so.salesOrderDate || "N/A"}`,
    expectedReceipt: `- **Expected Receipt:** ${so.expectedReceiptDate || "N/A"}`,
  };

  const lines = [
    `### ${orderId}`,
    "",
    fieldMap.status,
    fieldMap.totalPrice,
    fieldMap.quantity,
    fieldMap.openQuantity,
    fieldMap.buyer,
    fieldMap.warehouse,
    fieldMap.orderDate,
    fieldMap.expectedReceipt,
  ];

  if (requestedFields.size && !requestedFields.has("sku")) {
    return formatSelectedFields(orderId, requestedFields, fieldMap, lines);
  }

  if (skuBlock) {
    lines.push("", skuBlock);
  }

  return lines.join("\n");
}

function formatAsn(doc, userText = "") {
  const asn = doc.asnDetails || doc.asn || doc;
  const asnNumber = asn.asnNumber || doc.asnNumber || "ASN";
  const requestedFields = detectRequestedFields(userText);
  const skuBlock = formatSkuLines(doc, userText, "SKUs");

  if (
    requestedFields.has("sku") &&
    !requestedFields.has("totalPrice") &&
    !requestedFields.has("status") &&
    !requestedFields.has("openQuantity") &&
    !requestedFields.has("receivedQuantity")
  ) {
    return [`### ${asnNumber}`, "", skuBlock || "No SKU / line items found on this record."].join("\n");
  }

  const fieldMap = {
    totalPrice: `- **Total ASN Price:** ${formatAmount(asn.totalASNPrice || asn.totalPOValue)}`,
    status: `- **Receipt Status:** ${asn.receiptStatus || "N/A"}`,
    quantity: `- **Expected Qty:** ${asn.totalExpectedQty ?? "N/A"}`,
    expectedQuantity: `- **Expected Qty:** ${asn.totalExpectedQty ?? "N/A"}`,
    receivedQuantity: `- **Received Qty:** ${asn.totalReceivedQty ?? "N/A"}`,
    openQuantity: `- **Open Quantity:** ${
      asn.openQuantity ??
      (asn.totalExpectedQty != null && asn.totalReceivedQty != null
        ? Number(asn.totalExpectedQty) - Number(asn.totalReceivedQty)
        : "N/A")
    }`,
    warehouse: `- **Warehouse:** ${asn.warehouse || asn.warehouseId || "N/A"}`,
    supplier: `- **Supplier Ref:** ${doc.shipFrom?.supplierReference || "N/A"}`,
  };

  const lines = [
    `### ${asnNumber}`,
    "",
    fieldMap.status,
    fieldMap.totalPrice,
    fieldMap.expectedQuantity,
    fieldMap.receivedQuantity,
    fieldMap.warehouse,
    fieldMap.supplier,
  ];

  if (requestedFields.size && !requestedFields.has("sku")) {
    return formatSelectedFields(asnNumber, requestedFields, fieldMap, lines);
  }

  if (skuBlock) {
    lines.push("", skuBlock);
  }

  return lines.join("\n");
}

function formatGrn(doc, userText = "") {
  const header = doc.AsnHeader || doc;
  const grnNumber = header["GRN Number"] || header.grnNumber || "GRN";
  const asnNumber = header["ASN Number"] || header.asnNumber || "N/A";
  const requestedFields = detectRequestedFields(userText);
  const skuBlock = formatSkuLines(doc, userText, "SKUs");

  // SKU-focused answer (still include key GRN header when "all details" is requested)
  if (requestedFields.has("sku") && !/\ball\s+details\b/i.test(userText) && !requestedFields.has("totalPrice")) {
    return [
      `### ${grnNumber}`,
      "",
      `- **ASN Number:** ${asnNumber}`,
      "",
      skuBlock || "No SKU / line items found on this GRN.",
    ].join("\n");
  }

  const fieldMap = {
    status: `- **Type:** ${header.Type === 1 || header.Type === "1" ? "Standard" : header.Type || "N/A"}`,
    quantity: `- **Expected Qty:** ${header["Total Expected Qty"] ?? "N/A"}`,
    expectedQuantity: `- **Expected Qty:** ${header["Total Expected Qty"] ?? "N/A"}`,
    receivedQuantity: `- **Received Qty:** ${header["Total Received Qty"] ?? "N/A"}`,
    openQuantity: `- **Open Quantity:** ${
      header.openQuantity ??
      (header["Total Expected Qty"] != null && header["Total Received Qty"] != null
        ? Number(header["Total Expected Qty"]) - Number(header["Total Received Qty"])
        : "N/A")
    }`,
    warehouse: `- **Facility:** ${header["Facility Name"] || header.facilityName || "N/A"}`,
    buyer: `- **Owner:** ${header.Owner || header["Owner"] || "N/A"}`,
    orderDate: `- **Created Date:** ${header["Created Date"] || "N/A"}`,
  };

  const lines = [
    `### ${grnNumber}`,
    "",
    `- **ASN Number:** ${asnNumber}`,
    fieldMap.buyer,
    `- **Facility ID:** ${header["Facility ID"] || "N/A"}`,
    fieldMap.warehouse,
    fieldMap.expectedQuantity,
    fieldMap.receivedQuantity,
    `- **Expected Weight:** ${header["Total Expected Weight"] ?? "N/A"}`,
    `- **Expected Volume:** ${header["Total Expected Volume"] ?? "N/A"}`,
    fieldMap.status,
    fieldMap.orderDate,
  ];

  if (requestedFields.size && !requestedFields.has("sku") && !/\ball\s+details\b/i.test(userText)) {
    return formatSelectedFields(grnNumber, requestedFields, fieldMap, lines);
  }

  if (skuBlock || /\b(sku|all\s+details|with\s+sku)\b/i.test(userText)) {
    lines.push("", skuBlock || "No SKU / line items found on this GRN.");
  }

  return lines.join("\n");
}

function formatShipment(doc, userText = "") {
  const hdr = doc.shipmentDetails || doc.header || {};
  const src = doc.sourceDetail || {};
  const dest = doc.destination || {};
  const carr = doc.carrier || {};
  const load = doc.load || doc.loadDates || {};
  const shipmentNumber =
    hdr.shipmentNumber || doc.shipmentNumber || doc.shipmentOrder || "Shipment";
  const statusRaw = doc.status ?? hdr.status;
  const requestedFields = detectRequestedFields(userText);
  const skuBlock = formatSkuLines(doc, userText, "SKUs");

  if (requestedFields.has("sku") && !/\ball\s+details\b/i.test(userText) && !requestedFields.has("status")) {
    return [
      `### ${shipmentNumber}`,
      "",
      skuBlock || "No SKU / line items found on this shipment.",
    ].join("\n");
  }

  const fieldMap = {
    status: `- **Status:** ${shipmentStatusLabel(statusRaw)}`,
    quantity: `- **Total Qty:** ${hdr.totalQty ?? hdr.totalOrderedQuantity ?? "N/A"}`,
    warehouse: `- **Source:** ${hdr.source || src.sourceName || src.name || "N/A"}`,
    buyer: `- **Destination:** ${dest.destinationName || dest.name || dest.city || "N/A"}`,
    supplier: `- **Carrier:** ${carr.carrierName || "N/A"}`,
    orderDate: `- **Shipment Date:** ${hdr.shipmentDate || doc.createdAt || "N/A"}`,
    expectedReceipt: `- **Expected Arrival:** ${load.expectedArrivalDate || hdr.expectedArrivalDate || "N/A"}`,
  };

  const lines = [
    `### ${shipmentNumber}`,
    "",
    fieldMap.status,
    fieldMap.warehouse,
    fieldMap.buyer,
    `- **Destination City:** ${dest.city || "N/A"}`,
    fieldMap.supplier,
    `- **AWB Number:** ${carr.awbNumber || "N/A"}`,
    `- **Vehicle Number:** ${load.vehicleNumber || hdr.vehicleNumber || "N/A"}`,
    fieldMap.quantity,
    `- **Total Weight:** ${hdr.totalWeight ?? "N/A"}`,
    `- **Total Volume:** ${hdr.totalVolume ?? "N/A"}`,
    fieldMap.orderDate,
    fieldMap.expectedReceipt,
    `- **Actual Departure:** ${load.actualDepartureDate || "N/A"}`,
  ];

  if (requestedFields.size && !requestedFields.has("sku") && !/\ball\s+details\b/i.test(userText)) {
    return formatSelectedFields(shipmentNumber, requestedFields, fieldMap, lines);
  }

  if (skuBlock || /\b(sku|all\s+details|with\s+sku)\b/i.test(userText)) {
    lines.push("", skuBlock || "No SKU / line items found on this shipment.");
  }

  return lines.join("\n");
}

function formatItem(doc, userText = "") {
  const detail = doc.detail && typeof doc.detail === "object" ? doc.detail : {};
  const item = doc.item || doc;
  const skuId = detail.skuId || item.skuId || item.itemId || item.sku || item.code || "Item";
  const description = detail.description || item.description || item.name || "N/A";
  const requested = detectRequestedFields(userText);

  if (requested.has("sku") && !requested.has("status") && !requested.has("quantity")) {
    return [`- **SKU ID:** ${skuId}`, `  - **SKU Description:** ${description}`].join("\n");
  }

  return [
    `### ${skuId}`,
    "",
    `- **SKU Description:** ${description}`,
    `- **Ordered Qty:** ${detail.orderedQty ?? item.quantity ?? item.onHandQuantity ?? "N/A"}`,
    `- **Open Qty:** ${detail.openQty ?? "N/A"}`,
    `- **Purchase Order:** ${detail.purchaseOrder || "N/A"}`,
    `- **Warehouse:** ${doc.warehouse || item.warehouse || "N/A"}`,
  ].join("\n");
}

function formatDocument(doc, collection, userText = "") {
  if (!doc || typeof doc !== "object") return String(doc);

  if (collection === "grns" || doc.AsnHeader || doc.AsnDetail) return formatGrn(doc, userText);
  if (collection === "shipments" || doc.shipmentDetails) return formatShipment(doc, userText);
  if (collection === "purchaseorders" || doc.purchase_order) return formatPurchaseOrder(doc, userText);
  if (collection === "salesorders" || doc.soDetails || doc.sales_order || doc.salesOrder) {
    return formatSalesOrder(doc, userText);
  }
  if (collection === "asns" || doc.asnDetails) return formatAsn(doc, userText);
  if (collection === "items" || doc.item || doc.detail?.skuId) return formatItem(doc, userText);

  const pairs = Object.entries(doc)
    .filter(([, v]) => v != null && typeof v !== "object")
    .slice(0, 6)
    .map(([k, v]) => `- **${k}:** ${v}`);

  return pairs.length ? pairs.join("\n") : JSON.stringify(doc, null, 2);
}

function formatDocuments(data, collection, userText = "") {
  const docs = Array.isArray(data)
    ? data
    : Array.isArray(data?.documents)
      ? data.documents
      : Array.isArray(data?.results)
        ? data.results
        : null;

  if (!docs) return null;

  if (!docs.length) return "No matching records found.";

  const lines = [
    `Found **${docs.length}** ${collectionLabel(collection)}${docs.length === 1 ? "" : ""}:`,
    "",
  ];
  for (const doc of docs.slice(0, 20)) {
    lines.push(formatDocument(doc, collection, userText));
    lines.push("");
  }
  if (docs.length > 20) lines.push(`*Showing 20 of ${docs.length} records.*`);
  return lines.join("\n").trim();
}

function formatCount(data, collection = "") {
  const count =
    data?.count ??
    data?.document_count ??
    data?.total ??
    (typeof data === "number" ? data : null);

  if (count != null) {
    return `There ${Number(count) === 1 ? "is" : "are"} **${Number(count).toLocaleString()}** ${collectionLabel(collection)}.`;
  }
  return null;
}

function formatMetadata(data) {
  if (!data || typeof data !== "object") return null;

  const lines = ["## Partner Portal Data Guide", ""];

  for (const [collection, info] of Object.entries(data)) {
    if (!info || typeof info !== "object") continue;
    lines.push(`### ${collection}`);
    if (info.business_purpose) lines.push(info.business_purpose);
    if (Array.isArray(info.common_questions) && info.common_questions.length) {
      lines.push("", "**Example questions:**");
      for (const q of info.common_questions.slice(0, 4)) {
        lines.push(`- ${q}`);
      }
    }
    lines.push("");
  }

  return lines.join("\n").trim();
}

function formatServerError(text) {
  if (text.includes("mongodb://") || text.includes("mongodb+srv://")) {
    return [
      "I connected to the Partner Portal MCP server, but the database is not configured yet.",
      "",
      "Please ask your administrator to set the MongoDB connection string on the MCP server.",
    ].join("\n");
  }

  if (text.startsWith("Error calling tool")) {
    return text.replace(/^Error calling tool '[^']+':\s*/, "Unable to fetch data: ");
  }

  return text;
}

export function formatMcpToolText(toolName, text, collection = "", userText = "") {
  const raw = String(text || "").trim();
  if (!raw) return "No data returned.";

  if (/^Error calling tool/i.test(raw) || raw.includes("Invalid URI scheme")) {
    return formatServerError(raw);
  }

  const parsed = tryParseJson(raw);

  if (parsed != null) {
    const countText = formatCount(parsed, collection);
    if (countText) return countText;

    const docsText = formatDocuments(parsed, collection, userText);
    if (docsText) return docsText;

    const metaText = formatMetadata(parsed);
    if (metaText) return metaText;

    if (typeof parsed === "object" && !Array.isArray(parsed)) {
      return formatDocument(parsed, collection, userText);
    }
  }

  const { intro, data } = (() => {
    const start = raw.search(/[\[{]/);
    if (start < 0) return { intro: raw, data: null };
    try {
      return { intro: raw.slice(0, start).trim(), data: JSON.parse(raw.slice(start)) };
    } catch (_) {
      return { intro: raw, data: null };
    }
  })();

  if (Array.isArray(data)) {
    return formatDocuments(data, collection, userText) || raw;
  }

  if (data && typeof data === "object") {
    return (
      formatCount(data, collection) ||
      formatDocuments(data, collection, userText) ||
      formatMetadata(data) ||
      raw
    );
  }

  return intro || raw;
}

export function formatToolResult(toolName, result, collection = "", userText = "") {
  if (result?.isError) {
    const text = result?.content?.[0]?.text || "The request failed.";
    return formatMcpToolText(toolName, text, collection, userText);
  }

  const content = Array.isArray(result?.content) ? result.content : [];

  if (!content.length) {
    if (result?.structuredContent) {
      return formatMcpToolText(toolName, JSON.stringify(result.structuredContent), collection, userText);
    }
    return "No matching records found for your query.";
  }

  return content
    .map((item) => {
      if (item?.type === "text") return formatMcpToolText(toolName, item.text || "", collection, userText);
      if (item?.type === "image") return `[Image: ${item.mimeType || "image"}]`;
      return formatMcpToolText(toolName, JSON.stringify(item), collection, userText);
    })
    .filter(Boolean)
    .join("\n\n");
}
